import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  Form,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap"
import Select from "react-select"
import Dropzone from "react-dropzone"
import { Link } from "react-router-dom"
import { BiTrash } from "react-icons/bi"
import Swal from "sweetalert2"
import axios from "axios"
import _ from "lodash"
import { GENERATE_ENPT_REPORT_NEW } from "../../helpers/url_helper"
import { useGetEnptReportsQuery } from "store/enptReport/enptReport.api"
import LoadingSpinner from "../Reports/EditReports/EditTables/loader"
import { useDispatch, useSelector } from "react-redux"
import { getNewCustomers } from "store/old/customer/actions"
import { getNewOperators } from "store/old/operator/actions"
import { useLazyGetCountriesByCustomerQuery } from "store/country/country.api"
import { useLazyGetOperatorsByCountryAndCustomerQuery } from "store/operator/operator.api"
import { useGetCustomersQuery, useLazyGetSmscReceiversQuery } from "store/customer/customer.api"

import excelimage from "../../assets/images/excel.png"

const EnptReportGenerate = () => {
  const [smscFile, setSmscFile] = useState([])
  const [testerFile, setTesterFile] = useState([])
  const [otherMsgsFile, setOtherMsgsFile] = useState([])

  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedOperator, setSelectedOperator] = useState(null)
  const [receiver, setReceiver] = useState(null)

  const [countrySelectOptions, setCountrySelectOptions] = useState([])
  const [operatorSelectOptions, setOperatorSelectOptions] = useState([])
  const [customerSelectOptions, setCustomerSelectOptions] = useState([])
  const [receiverSelectOptions, setReceiverSelectOptions] = useState([])

  const [resultsTimezone, setResultsTimezone] = useState("GMT+0")
  const [testersTimezone, setTestersTimezone] = useState("GMT+5")
  const [phonesTimezone, setPhonesTimezone] = useState("GMT+0")
  const dispatch = useDispatch()
  const [appendToReportId, setAppendToReportId] = useState("")
  const [appendToReportName, setAppendToReportName] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [filters, setFilters] = useState({ customer: "", country: "", operator: "" })

  // Cascading RTK Queries for SMSC route configuration
  const { data: customersData, isLoading: isCustomersLoading } = useGetCustomersQuery()
  const [fetchCountries, { data: countriesData, isFetching: isCountriesLoading }] = useLazyGetCountriesByCustomerQuery()
  const [fetchOperators, { data: operatorsData, isFetching: isOperatorsLoading }] = useLazyGetOperatorsByCountryAndCustomerQuery()
  const [fetchReceivers, { data: receiversData, isFetching: isReceiversLoading }] = useLazyGetSmscReceiversQuery()

  // Cascading reset change handlers
  const handleCustomerChange = (customer) => {
    setSelectedCustomer(customer)
    setSelectedCountry(null)
    setSelectedOperator(null)
    setReceiver(null)
    setCountrySelectOptions([])
    setOperatorSelectOptions([])
    setReceiverSelectOptions([])
  }

  const handleCountryChange = (country) => {
    setSelectedCountry(country)
    setSelectedOperator(null)
    setReceiver(null)
    setOperatorSelectOptions([])
    setReceiverSelectOptions([])
  }

  const handleOperatorChange = (operator) => {
    setSelectedOperator(operator)
    setReceiver(null)
    setReceiverSelectOptions([])
  }

  // Handle Customer selection and fetch related Countries
  useEffect(() => {
    if (selectedCustomer) {
      fetchCountries(selectedCustomer.value)
    } else {
      setCountrySelectOptions([])
      setSelectedCountry(null)
    }
  }, [selectedCustomer, fetchCountries])

  // Populate Countries based on Customer selection
  useEffect(() => {
    if (countriesData) {
      const data = Array.isArray(countriesData)
        ? countriesData
        : _.get(countriesData, "data") || _.get(countriesData, "countries") || _.get(countriesData, "message") || []
      setCountrySelectOptions(
        data.map(c => ({
          value: c.id || c.value,
          label: c.name || c.country || c.label,
        }))
      )
    }
  }, [countriesData])

  // Populate Customers
  useEffect(() => {
    if (customersData) {
      const data = Array.isArray(customersData)
        ? customersData
        : _.get(customersData, "data") || _.get(customersData, "customers") || _.get(customersData, "message") || []
      setCustomerSelectOptions(
        data.map(c => ({
          value: c.id?.toString() || c.value,
          label: c.name || c.label,
        }))
      )
    }
  }, [customersData])

  // Handle Country and Customer selection and fetch related Operators
  useEffect(() => {
    if (selectedCountry && selectedCustomer) {
      fetchOperators({ country_id: selectedCountry.value, customer_id: selectedCustomer.value })
    } else {
      setOperatorSelectOptions([])
      setSelectedOperator(null)
    }
  }, [selectedCountry, selectedCustomer, fetchOperators])

  // Populate Operators based on Country and Customer selection
  useEffect(() => {
    if (operatorsData) {
      const data = Array.isArray(operatorsData)
        ? operatorsData
        : _.get(operatorsData, "data") || _.get(operatorsData, "operators") || _.get(operatorsData, "message") || []
      setOperatorSelectOptions(
        data.map(o => ({
          value: o.id?.toString() || o.value,
          label: o.operator_name ? `${o.operator_name} - ${o.country}` : o.label || o.name,
        }))
      )
    }
  }, [operatorsData])

  // Handle Operator and Customer selection and fetch Receivers
  useEffect(() => {
    if (selectedOperator && selectedCustomer) {
      fetchReceivers({ operator_id: selectedOperator.value, customer_id: selectedCustomer.value })
    } else {
      setReceiverSelectOptions([])
      setReceiver(null)
    }
  }, [selectedOperator, selectedCustomer, fetchReceivers])

  // Populate Receivers
  useEffect(() => {
    if (receiversData && receiversData.contacts) {
      setReceiverSelectOptions(
        receiversData.contacts.map(contact => ({
          value: contact.phone,
          label: `${contact.phone} (${contact.location || "Unknown"})`,
        }))
      )
    } else if (receiversData) {
      const data = Array.isArray(receiversData)
        ? receiversData
        : _.get(receiversData, "data") || _.get(receiversData, "contacts") || []
      setReceiverSelectOptions(
        data.map(contact => ({
          value: contact.phone || contact.value,
          label: contact.label
            ? contact.label
            : contact.phone
            ? `${contact.phone} (${contact.location || "Unknown"})`
            : "Unknown",
        }))
      )
    }
  }, [receiversData])

  useEffect(() => {
    dispatch(getNewCustomers())
    dispatch(getNewOperators())
  }, [dispatch])

  const customersRaw = useSelector(state => state.customer.customers || [])
  const operatorsRaw = useSelector(state => state.operator.operators || [])

  const customers = customersRaw.map(obj => ({
    id: obj.id,
    label: obj.name,
    value: obj.id,
  }))
  // Extract unique countries from operatorsRaw
  const uniqueCountries = [...new Set(operatorsRaw.map(op => op.country).filter(Boolean))].sort();
  const countryOptions = [{ value: "", label: "All Countries" }, ...uniqueCountries.map(c => ({ value: c, label: c }))];

  // Filter operators by selected country if any
  const filteredOperatorsRaw = filters.country 
    ? operatorsRaw.filter(op => op.country === filters.country) 
    : operatorsRaw;

  const operators = filteredOperatorsRaw.map(obj => ({
    id: obj.id,
    label: obj.operator_name + " " + obj.country,
    value: obj.id,
  }))

  const customerOptions = [{ value: "", label: "All Customers" }, ...customers]
  const operatorOptions = [{ value: "", label: "All Operators" }, ...operators]

  const getOperatorFullName = (report) => {
    if (!report) return "";
    const operatorObj = operatorsRaw.find(op => op.id === report.internal_operator_id);
    if (operatorObj && operatorObj.country && report.operator && !report.operator.includes(operatorObj.country)) {
      return `${report.operator} ${operatorObj.country}`;
    }
    return report.operator || "";
  };

  // Fetch reports for recent reports table
  const { data: reportsData, refetch: refetchReports } = useGetEnptReportsQuery({ page: 1, limit: 10 })

  // Fetch reports for the modal with filters
  const { data: modalReportsData, isLoading: isLoadingModalReports } = useGetEnptReportsQuery({
    page: 1, limit: 50, customer: filters.customer, operator: filters.operator, country: filters.country
  })

  const recentUploads = reportsData?.reports?.slice(0, 5) || []

  const timezones = [
    { value: "GMT-12", label: "GMT-12" },
    { value: "GMT-11", label: "GMT-11" },
    { value: "GMT-10", label: "GMT-10" },
    { value: "GMT-9", label: "GMT-9" },
    { value: "GMT-8", label: "GMT-8" },
    { value: "GMT-7", label: "GMT-7" },
    { value: "GMT-6", label: "GMT-6" },
    { value: "GMT-5", label: "GMT-5" },
    { value: "GMT-4", label: "GMT-4" },
    { value: "GMT-3", label: "GMT-3" },
    { value: "GMT-2", label: "GMT-2" },
    { value: "GMT-1", label: "GMT-1" },
    { value: "GMT+0", label: "GMT+0" },
    { value: "GMT+1", label: "GMT+1" },
    { value: "GMT+2", label: "GMT+2" },
    { value: "GMT+3", label: "GMT+3" },
    { value: "GMT+4", label: "GMT+4" },
    { value: "GMT+5", label: "GMT+5" },
    { value: "GMT+6", label: "GMT+6" },
    { value: "GMT+7", label: "GMT+7" },
    { value: "GMT+8", label: "GMT+8" },
    { value: "GMT+9", label: "GMT+9" },
    { value: "GMT+10", label: "GMT+10" },
    { value: "GMT+11", label: "GMT+11" },
    { value: "GMT+12", label: "GMT+12" },
  ]

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  const handleAcceptedFile = (files, setFileState) => {
    files.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    )
    setFileState(files)
  }

  const validateSmscFile = file => {
    var x = file.path?.split(".") || file.name?.split(".")
    let extension = x[x.length - 1]?.toLowerCase()
    const allowed = ["xlsx", "xls", "csv"]
    if (allowed.includes(extension)) {
      return null
    } else {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: `Please Select ${allowed.map(e => e.toUpperCase()).join(" or ")} File to Continue`,
      })
      return {
        error: "Please Select Supported File to Continue",
      }
    }
  }

  const validateOtherFile = file => {
    var x = file.path?.split(".") || file.name?.split(".")
    let extension = x[x.length - 1]?.toLowerCase()
    if (extension === "xlsx" || extension === "csv") {
      return null
    } else {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please Select XLSX or CSV File to Continue",
      })
      return {
        error: "Please Select SpreadSheet File to Continue",
      }
    }
  }

  const renderFilePreview = (files, title, supportText, onRemove) => {
    return (
      <div className="col-12">
        {files.length === 0 ? (
          <Row>
            <div className="col-12">
              <div>
                <h6 className="file_text">
                  <i
                    className=" bx bx-paperclip "
                    style={{ rotate: "140deg" }}
                  />
                  &nbsp; No file added
                </h6>
              </div>
              <div className="support_text text-wrap"> {supportText}</div>
              <div className="support_text text-wrap"> Maximum file size of 20mb</div>
            </div>
          </Row>
        ) : (
          files.map((f, i) => {
            let displayName = f.name
            let MAX_NAME_LENGTH = 45
            if (displayName.length > MAX_NAME_LENGTH) {
              const ellipsis = "..."
              const sliceLength = (MAX_NAME_LENGTH - ellipsis.length) / 2
              displayName = `${displayName.slice(
                0,
                sliceLength
              )}${ellipsis}${displayName.slice(-sliceLength)}`
            }
            return (
              <Card
                style={{ boxShadow: "none" }}
                className="ms-4 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                key={i + "-file"}
              >
                <div className="">
                  <Row className="align-items-center">
                    <Col className="col-auto ">
                      <img
                        data-dz-thumbnail=""
                        height="68"
                        className="rounded bg-light"
                        src={excelimage}
                      />
                    </Col>
                    <Col>
                      <Link
                        to="#"
                        className="text-muted font-weight-bold truncate-text"
                      >
                        {displayName}
                      </Link>
                      <p className="mb-0">
                        <strong>{f.formattedSize}</strong>
                      </p>
                    </Col>
                    <Col className="col-auto">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={onRemove}
                      >
                        <BiTrash />
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Card>
            )
          })
        )}
      </div>
    )
  }

  const handleGenerate = async () => {
    if (
      smscFile.length === 0 ||
      testerFile.length === 0 ||
      !selectedCustomer ||
      !selectedCountry ||
      !selectedOperator ||
      !receiver ||
      !resultsTimezone ||
      !testersTimezone ||
      !phonesTimezone
    ) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please upload the required files (SMSC Inspector and Tester), fill in all route details (Customer, Country, Operator, Receiver), and select all timezones.",
      })
      return
    }

    // Clean the receiver value by removing + and any spaces or non-digit characters
    const receiverValue = (receiver.value || "").toString().replace(/\D/g, "")
    if (!/^\d+$/.test(receiverValue) || receiverValue === "") {
      Swal.fire({
        icon: "warning",
        title: "Invalid Receiver",
        text: "Receiver number must contain only digits. Do not include '+' or spaces.",
      })
      return
    }

    const operatorName = selectedOperator.label ? selectedOperator.label.split(" - ")[0] : ""

    const formData = new FormData()
    formData.append("smscFile", smscFile[0])
    formData.append("customer", selectedCustomer.label)
    formData.append("country", selectedCountry.label)
    formData.append("operator", operatorName)
    formData.append("receiver", receiverValue)
    formData.append("tester", testerFile[0])
    if (otherMsgsFile.length > 0) {
      formData.append("otherMsgs", otherMsgsFile[0])
    }
    formData.append("resultsTimezone", resultsTimezone)
    formData.append("testersTimezone", testersTimezone)
    formData.append("phonesTimezone", phonesTimezone)
    if (appendToReportId) {
      formData.append("appendToReportId", appendToReportId)
    }

    submitReport(formData)
  }

  const submitReport = async (formData) => {
    Swal.fire({
      title: "Generating Report...",
      text: "Please wait while the report is being processed.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })

    try {
      const response = await axios.post(GENERATE_ENPT_REPORT_NEW, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
      })

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Report generated successfully!",
        }).then(() => {
          refetchReports()
          setSmscFile([])
          setTesterFile([])
          setOtherMsgsFile([])
          setSelectedCustomer(null)
          setSelectedCountry(null)
          setSelectedOperator(null)
          setReceiver(null)
          setAppendToReportId("")
          setAppendToReportName("")
        })
      } else if (response.data.isValidationError) {
        const warnings = response.data.warnings;
        const { unallowedEnterprises, allowedEnterprisesId, standardRenamesId } = warnings;
        const allowedLink = `https://docs.google.com/spreadsheets/d/${allowedEnterprisesId}/edit`;
        const renamesLink = `https://docs.google.com/spreadsheets/d/${standardRenamesId}/edit`;

        Swal.fire({
          icon: "warning",
          title: "Unrecognized Enterprises Found",
          width: "600px",
          html: `
            <p style="font-size: 15px; margin-bottom: 15px;">
              The report <b>was NOT generated</b> because these names from the Tester file are unrecognized:
            </p>
            <div style="max-height: 180px; overflow-y: auto; text-align: left; background: #f8f9fa; border: 1px solid #e9ecef; padding: 12px; border-radius: 6px; margin-bottom: 20px;">
              <ul style="margin: 0; padding-left: 20px; color: #d9534f; font-weight: 500;">
                ${unallowedEnterprises.map(ent => `<li style="margin-bottom: 4px;">${ent}</li>`).join("")}
              </ul>
            </div>
            <p style="font-size: 14px; color: #6c757d; margin-bottom: 15px;">
              Please update the respective Google Sheet, then try generating the report again.
            </p>
            <div style="display: flex; justify-content: center; gap: 15px; margin-top: 10px;">
              <a href="${allowedLink}" target="_blank" class="btn px-4 py-2 shadow-sm" style="background-color: #dc3741; color: white; border: none; text-decoration: none; border-radius: 4px;"><i class="fas fa-plus-circle me-1"></i> Add New Brand</a>
              <a href="${renamesLink}" target="_blank" class="btn px-4 py-2 shadow-sm" style="background-color: #dc3741; color: white; border: none; text-decoration: none; border-radius: 4px;"><i class="fas fa-edit me-1"></i> Fix Misspelled Brand</a>
            </div>
          `,
          showConfirmButton: false,
          showCloseButton: true
        })
      } else if (response.data.isDateValidationError) {
        const { dateWarnings } = response.data;
        const { isDifferentDates, differentDates, isUnsupportedDates, unsupportedDateSamples, isUnsupportedTz, unsupportedTzSamples } = dateWarnings || {};

        if (isDifferentDates) {
          Swal.fire({
            icon: "warning",
            title: "Different Dates Detected in Inspector File",
            width: "550px",
            html: `
              <p style="font-size: 15px; margin-bottom: 12px; text-align: left;">
                The uploaded <b>SMSC Inspector File</b> contains test records across <b>multiple different dates</b>:
              </p>
              <div style="background: #fff3cd; border: 1px solid #ffeeba; border-radius: 6px; padding: 12px; margin-bottom: 15px; text-align: left;">
                <ul style="margin: 0; padding-left: 20px; color: #856404; font-weight: 600;">
                  ${(differentDates || []).map(d => `<li style="margin-bottom: 4px;">${d}</li>`).join("")}
                </ul>
              </div>
              <p style="font-size: 13.5px; color: #6c757d; margin-bottom: 0; text-align: left;">
                ⚠️ ENPT test runs require all messages to belong to a <b>single test date</b>. Please ensure your inspector file contains only messages from one test day.
              </p>
            `,
            showCancelButton: true,
            confirmButtonText: "Continue",
            cancelButtonText: "Cancel to Fix",
            reverseButtons: true,
            confirmButtonColor: "#f1b44c",
            cancelButtonColor: "#74788d",
            allowOutsideClick: false,
          }).then((result) => {
            if (result.isConfirmed) {
              formData.set("ignoreDateWarnings", "true");
              submitReport(formData);
            }
          });
          return;
        }

        let warningTitle = "Date Issue Detected";
        let warningHtml = "";

        if (isUnsupportedDates) {
          warningTitle = "Unsupported Date Format in Inspector File";
          const samples = (unsupportedDateSamples || []).map(s => `Row ${s.row}: "${s.value}"`).join("<br>");
          warningHtml = `
            <p style="font-size: 15px; margin-bottom: 12px; text-align: left;">
              The uploaded file contains dates in an <b>unsupported format</b>:
            </p>
            <div style="background: #f8d7da; border: 1px solid #f5c6cb; border-radius: 6px; padding: 12px; margin-bottom: 15px; text-align: left; color: #721c24; font-family: monospace; font-size: 13px;">
              ${samples}
            </div>
            <p style="font-size: 13.5px; color: #6c757d; margin-bottom: 0; text-align: left;">
              <b>Expected Format:</b> <code>17 Jun 2026 9:54:02</code> (e.g. <code>D MMM YYYY H:mm:ss</code>)
            </p>
          `;
        } else if (isUnsupportedTz) {
          warningTitle = "Unsupported Timezone Format in Inspector File";
          const samples = (unsupportedTzSamples || []).map(s => `Row ${s.row}: "${s.value}"`).join("<br>");
          warningHtml = `
            <p style="font-size: 15px; margin-bottom: 12px; text-align: left;">
              The uploaded file contains timezones in an <b>unsupported format</b>:
            </p>
            <div style="background: #f8d7da; border: 1px solid #f5c6cb; border-radius: 6px; padding: 12px; margin-bottom: 15px; text-align: left; color: #721c24; font-family: monospace; font-size: 13px;">
              ${samples}
            </div>
            <p style="font-size: 13.5px; color: #6c757d; margin-bottom: 0; text-align: left;">
              <b>Expected Format:</b> <code>+02:00</code> or <code>+2:00</code>
            </p>
          `;
        }

        Swal.fire({
          icon: "warning",
          title: warningTitle,
          width: "550px",
          html: warningHtml,
          confirmButtonColor: "#f1b44c",
          confirmButtonText: "OK",
        });
      } else {
        throw new Error(response.data.message || "Failed to generate report")
      }
    } catch (error) {
      console.error("Error generating report:", error)
      Swal.fire({
        icon: "error",
        title: "Generation Failed",
        text: error.response?.data?.error || error.message || "An error occurred during generation.",
      })
    }
  }

  return (
    <React.Fragment>
      {/* 3 Upload Boxes */}
      <Row className="mb-4">
        {/* Slot 1: SMSC Inspector File */}
        <Col lg="4" className="mb-4 mb-lg-0">
          <div className="pe-4">
            <div className="pb-1">
              <h5>SMSC Inspector File</h5>
            </div>
          </div>
          <Card className="card_new2">
            <CardBody>
              <Form>
                <Dropzone
                  className="dropzon1"
                  maxFiles={1}
                  multiple={false}
                  accept={[".xlsx", ".xls", ".csv"]}
                  validator={validateSmscFile}
                  onDrop={acceptedFiles => {
                    handleAcceptedFile(acceptedFiles, setSmscFile)
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div className="dropzone1">
                      <div
                        className="dz-message needsclick"
                        style={{ marginTop: "5px" }}
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        <div className="mb-1 box3">
                          <i className="upload_icon fas fa-cloud-upload-alt"></i>
                        </div>
                        <h4>
                          Drop files here or{" "}
                          <span className="upload_text">click to upload </span>
                        </h4>
                      </div>
                    </div>
                  )}
                </Dropzone>
                <div
                  className="dropzone-previews mt-3"
                  id="file-previews"
                >
                  {renderFilePreview(
                    smscFile,
                    "SMSC Inspector",
                    "Supports: XLSX, XLS, CSV",
                    () => setSmscFile([])
                  )}
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>

        {/* Slot 2: Tester File */}
        <Col lg="4" className="mb-4 mb-lg-0">
          <div className="pe-4">
            <div className="pb-1">
              <h5>Tester File</h5>
            </div>
          </div>
          <Card className="card_new2">
            <CardBody>
              <Form>
                <Dropzone
                  className="dropzon1"
                  maxFiles={1}
                  multiple={false}
                  accept={[".xlsx", ".csv"]}
                  validator={validateOtherFile}
                  onDrop={acceptedFiles => {
                    handleAcceptedFile(acceptedFiles, setTesterFile)
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div className="dropzone1">
                      <div
                        className="dz-message needsclick"
                        style={{ marginTop: "5px" }}
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        <div className="mb-1 box3">
                          <i className="upload_icon fas fa-cloud-upload-alt"></i>
                        </div>
                        <h4>
                          Drop files here or{" "}
                          <span className="upload_text">click to upload </span>
                        </h4>
                      </div>
                    </div>
                  )}
                </Dropzone>
                <div
                  className="dropzone-previews mt-3"
                  id="file-previews"
                >
                  {renderFilePreview(testerFile, "Tester", "Supports: XLSX, CSV", () => setTesterFile([]))}
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>

        {/* Slot 3: Other Msgs File */}
        <Col lg="4" className="mb-4 mb-lg-0">
          <div className="pe-4">
            <div className="pb-1">
              <h5>Other Msgs File</h5>
            </div>
          </div>
          <Card className="card_new2">
            <CardBody>
              <Form>
                <Dropzone
                  className="dropzon1"
                  maxFiles={1}
                  multiple={false}
                  accept={[".xlsx", ".csv"]}
                  validator={validateOtherFile}
                  onDrop={acceptedFiles => {
                    handleAcceptedFile(acceptedFiles, setOtherMsgsFile)
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div className="dropzone1">
                      <div
                        className="dz-message needsclick"
                        style={{ marginTop: "5px" }}
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        <div className="mb-1 box3">
                          <i className="upload_icon fas fa-cloud-upload-alt"></i>
                        </div>
                        <h4>
                          Drop files here or{" "}
                          <span className="upload_text">click to upload </span>
                        </h4>
                      </div>
                    </div>
                  )}
                </Dropzone>
                <div
                  className="dropzone-previews mt-3"
                  id="file-previews"
                >
                  {renderFilePreview(otherMsgsFile, "Other Msgs", "Supports: XLSX, CSV (Optional)", () => setOtherMsgsFile([]))}
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* SMSC Route Details */}
      <Row className="mt-4 mb-3">
        <Col lg="3" md="6" className="mb-3 mb-lg-0">
          <label className="mb-2">
            Customer <span className="text-danger">*</span>
          </label>
          <Select
            value={selectedCustomer}
            onChange={handleCustomerChange}
            options={customerSelectOptions}
            isLoading={isCustomersLoading}
            placeholder="Select Customer..."
            isClearable
            className="basic-single"
            classNamePrefix="select"
          />
        </Col>
        <Col lg="3" md="6" className="mb-3 mb-lg-0">
          <label className="mb-2">
            Country <span className="text-danger">*</span>
          </label>
          <Select
            value={selectedCountry}
            onChange={handleCountryChange}
            options={countrySelectOptions}
            isLoading={isCountriesLoading}
            placeholder={
              isCountriesLoading
                ? "Loading countries..."
                : selectedCustomer
                ? "Select Country..."
                : "Select Customer first"
            }
            isDisabled={!selectedCustomer || isCountriesLoading}
            isClearable
            className="basic-single"
            classNamePrefix="select"
          />
        </Col>
        <Col lg="3" md="6" className="mb-3 mb-lg-0">
          <label className="mb-2">
            Operator <span className="text-danger">*</span>
          </label>
          <Select
            value={selectedOperator}
            onChange={handleOperatorChange}
            options={operatorSelectOptions}
            isLoading={isOperatorsLoading}
            placeholder={
              isOperatorsLoading
                ? "Loading operators..."
                : selectedCountry
                ? "Select Operator..."
                : "Select Country first"
            }
            isDisabled={!selectedCountry || isOperatorsLoading}
            isClearable
            className="basic-single"
            classNamePrefix="select"
          />
        </Col>
        <Col lg="3" md="6" className="mb-3 mb-lg-0">
          <label className="mb-2">
            Receiver <span className="text-danger">*</span>
          </label>
          <Select
            value={receiver}
            onChange={setReceiver}
            options={receiverSelectOptions}
            isLoading={isReceiversLoading}
            placeholder={
              isReceiversLoading
                ? "Loading receivers..."
                : selectedOperator
                ? "Select Receiver..."
                : "Select Operator first"
            }
            isDisabled={!selectedOperator || isReceiversLoading}
            isClearable
            className="basic-single"
            classNamePrefix="select"
          />
        </Col>
      </Row>

      <Row className="mt-4 mb-4 align-items-end">
        <Col lg="3" md="6" className="mb-3 mb-lg-0">
          <label className="mb-2">Results Timezone</label>
          <Select
            placeholder="Select Timezone"
            className="basic-single"
            classNamePrefix="select"
            isSearchable={true}
            name="resultsTimezone"
            options={timezones}
            value={timezones.find(opt => opt.value === resultsTimezone)}
            onChange={e => setResultsTimezone(e.value)}
          />
        </Col>
        <Col lg="3" md="6" className="mb-3 mb-lg-0">
          <label className="mb-2">Testers Timezone</label>
          <Select
            placeholder="Select Timezone"
            className="basic-single"
            classNamePrefix="select"
            isSearchable={true}
            name="testersTimezone"
            options={timezones}
            value={timezones.find(opt => opt.value === testersTimezone)}
            onChange={e => setTestersTimezone(e.value)}
          />
        </Col>
        <Col lg="3" md="6" className="mb-3 mb-lg-0">
          <label className="mb-2">Phones Timezone</label>
          <Select
            placeholder="Select Timezone"
            className="basic-single"
            classNamePrefix="select"
            isSearchable={true}
            name="phonesTimezone"
            options={timezones}
            value={timezones.find(opt => opt.value === phonesTimezone)}
            onChange={e => setPhonesTimezone(e.value)}
          />
        </Col>
        <Col lg="3" md="6" className="d-flex justify-content-lg-end mb-3 mb-lg-0">
          <Button
            color="danger"
            className="btn btn-custom px-4 py-2 w-100"
            onClick={handleGenerate}
          >
            Generate Report
          </Button>
        </Col>
      </Row>

      {/* 
      <Row className="mb-4 align-items-end">
        <Col lg="6" md="6" className="mb-3 mb-lg-0">
          <label className="mb-2">Append to Existing Report </label>
          <div className="d-flex align-items-center">
            {appendToReportId ? (
              <div className="d-flex align-items-center border p-2 rounded w-100 bg-light">
                <span className="me-auto text-truncate">
                  <strong>ID: {appendToReportId}</strong> {appendToReportName && `- ${appendToReportName}`}
                </span>
                <Button color="danger" size="sm" onClick={() => { setAppendToReportId(""); setAppendToReportName(""); }}>
                  <i className="mdi mdi-close"></i> Remove
                </Button>
              </div>
            ) : (
              <Button color="danger" className="px-4 py-2 w-100" onClick={() => setIsModalOpen(true)}>
                Select Report to Append
              </Button>
            )}
          </div>
        </Col>
      </Row>
      */}

      <Row style={{ marginTop: "50px" }}>
        <h5 style={{ paddingTop: "10px", fontWeight: "bold" }}>
          Recently Generated Reports
        </h5>
        <Col lg="12">
          <Card style={{ borderRadius: "10px" }}>
            <div
              style={{ borderRadius: "10px" }}
              className="table-responsive"
              hidden={!recentUploads?.length > 0}
            >
              <Table
                style={{ backgroundColor: "white" }}
                className="align-middle mb-0"
              >
                <thead>
                  <tr>
                    <th>Report ID</th>
                    {/* <th>Report Name</th> */}
                    <th>Customer</th>
                    <th>Operator</th>
                    <th>Tested Date</th>
                    <th className="text-center">Generated By</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUploads?.map((report, idx) => (
                    <tr key={idx}>
                      <td>{report.id}</td>
                      {/* <td>{report.name || `${report.customer} - ${getOperatorFullName(report)}_${report.test_date?.slice(0, 10) || report.createdAt?.slice(0, 10) || "N/A"}`}</td> */}
                      <td>{report.customer}</td>
                      <td>{getOperatorFullName(report)}</td>
                      <td>{report.test_date?.slice(0, 10) || report.createdAt?.slice(0, 10)}</td>
                      <td className="text-center">
                        {report.users_new?.name || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {recentUploads?.length === 0 && (
              <div className="p-4 text-center text-muted">
                No recent reports generated.
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Select Report Modal */}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} size="lg">
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
          Select Report to Append To
        </ModalHeader>
        <ModalBody>
          <Row className="mb-4">
            <Col md="4">
              <Select
                placeholder="Customer"
                classNamePrefix="select"
                isSearchable
                options={customerOptions}
                value={customerOptions.find(opt => opt.value === filters.customer)}
                onChange={(selected) => {
                  setFilters({ ...filters, customer: selected?.value || "", operator: "" })
                  if (selected?.value) {
                    dispatch(getNewOperators({ customer_id: selected.value }))
                  } else {
                    dispatch(getNewOperators())
                  }
                }}
              />
            </Col>
            <Col md="4">
              <Select
                placeholder="Country"
                classNamePrefix="select"
                isSearchable
                options={countryOptions}
                value={countryOptions.find(opt => opt.value === filters.country)}
                onChange={(selected) => setFilters({ ...filters, country: selected?.value || "", operator: "" })}
              />
            </Col>
            <Col md="4">
              <Select
                placeholder="Operator"
                classNamePrefix="select"
                isSearchable
                options={operatorOptions}
                value={operatorOptions.find(opt => opt.value === filters.operator)}
                onChange={(selected) => setFilters({ ...filters, operator: selected?.value || "" })}
              />
            </Col>
          </Row>
          {isLoadingModalReports ? (
            <div className="text-center py-4"><LoadingSpinner /></div>
          ) : modalReportsData && modalReportsData.reports && modalReportsData.reports.length > 0 ? (
            <div className="table-responsive">
              <Table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Report ID</th>
                    <th>Customer</th>
                    <th>Operator</th>
                    <th>Date</th>
                    <th className="text-center">Generated By</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {modalReportsData.reports.map((report) => (
                    <tr key={report.id}>
                      <td>{report.id}</td>
                      <td>{report.customer}</td>
                      <td>{getOperatorFullName(report)}</td>
                      <td>{report.test_date?.slice(0, 10) || report.createdAt?.slice(0, 10)}</td>
                      <td className="text-center">{report.users_new?.name || "-"}</td>
                      <td>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => {
                            setAppendToReportId(report.id)
                            setAppendToReportName(report.name || `${report.customer} - ${getOperatorFullName(report)}_${report.test_date?.slice(0, 10) || report.createdAt?.slice(0, 10) || "N/A"}`)
                            setIsModalOpen(false)
                          }}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4">No reports found.</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  )
}

export default EnptReportGenerate
